import clsx from 'clsx';
import { isEqual } from 'lodash';
import { addLocale } from 'primereact/api';
import { Calendar as PrimeReactCalendar } from 'primereact/calendar';
import type { FormEvent } from 'primereact/ts-helpers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useTestTag from '../../../common/hooks/use-test-tag';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import OdinIcon, { OdinIconSize } from '../../odin-icon/odin-icon.component';
import { resolveLocale } from './calendar-functions';

export type OdinCalendarSelectionMode = 'single' | 'multiple' | 'range';

export interface OdinCalendarProps extends DynamicFieldComponentProps<Date | Date[] | null> {
  showIcon?: boolean;
  iconPosiiton?: 'left' | 'right';
  showTime?: boolean;
  hourFormat?: '12' | '24';
  timeOnly?: boolean;
  readOnlyInput?: boolean;
  showButtonBar?: boolean;
  appendTo?: 'self' | HTMLElement | undefined | null;
  selectionMode?: OdinCalendarSelectionMode;
}

function OdinCalendar({
  label,
  labelStyles = 'odin-text-sm odin-font-bold',
  value,
  onChange,
  required,
  readOnly,
  error,
  showIcon = true,
  iconPosiiton = 'left',
  showTime = false,
  hourFormat,
  timeOnly = false,
  readOnlyInput = false,
  showButtonBar = false,
  appendTo = 'self',
  selectionMode = 'single',
  placeholder,
}: OdinCalendarProps) {
  const [valueState, setValueState] = useState(value);
  const [locale, setLocale] = useState<string>();

  const calendarRef = useRef<PrimeReactCalendar>(null);

  useEffect(() => {
    resolveLocale().then(({ locale, options }) => {
      addLocale(locale, options as object);
      setLocale(locale);
    });
  }, []);

  useEffect(() => {
    if (!isEqual(value, valueState)) {
      setValueState(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only sync when value prop changes, check state to prevent redundant updates
  }, [value]);

  const tagCreator = useTestTag(label || '');

  const handleChange = useCallback(
    (event: FormEvent) => {
      if (event.originalEvent && event.originalEvent.type === 'input' && !event.value && !valueState) {
        return;
      }

      setValueState(event.value);
      onChange?.(event.value);
    },
    [onChange, valueState]
  );

  const icon = useMemo(
    () =>
      timeOnly === true ? (
        <OdinIcon size={OdinIconSize.ExtraSmall} icon="Clock" />
      ) : (
        <OdinIcon size={OdinIconSize.ExtraSmall} icon="Calendar" />
      ),
    [timeOnly]
  );

  const decreaseYear = useCallback(() => {
    const currentViewDate = calendarRef.current?.getViewDate() as Date;

    const newViewDate = new Date(currentViewDate);
    newViewDate.setFullYear(newViewDate.getFullYear() - 1);

    calendarRef.current?.updateViewDate(null, newViewDate);
  }, []);

  const increaseYear = useCallback(() => {
    const currentViewDate = calendarRef.current?.getViewDate() as Date;

    const newViewDate = new Date(currentViewDate);
    newViewDate.setFullYear(newViewDate.getFullYear() + 1);

    calendarRef.current?.updateViewDate(null, newViewDate);
  }, []);

  const headerTemplate = useCallback(() => {
    return (
      <>
        <button
          className="odin-flex odin-justify-center odin-items-center p-datepicker-year-prev"
          onClick={decreaseYear}
        >
          <OdinIcon icon="ChevronsLeft" />
        </button>
        <button
          className="odin-flex odin-justify-center odin-items-center odin-order-1 p-datepicker-year-next"
          onClick={increaseYear}
        >
          <OdinIcon icon="ChevronsRight" />
        </button>
      </>
    );
  }, [decreaseYear, increaseYear]);

  const prevIcon = useMemo(() => <OdinIcon icon="ChevronLeft" />, []);
  const nextIcon = useMemo(() => <OdinIcon icon="ChevronRight" />, []);
  const incrementIcon = useMemo(() => <OdinIcon icon="ChevronUp" />, []);
  const decrementIcon = useMemo(() => <OdinIcon icon="ChevronDown" />, []);

  return (
    <div className="odin-flex odin-flex-col odin-w-full" ref={tagCreator('calendar')}>
      {label && (
        <div className="odin-flex">
          <FieldLabel label={label} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {locale && (
        <PrimeReactCalendar
          ref={calendarRef}
          className={clsx('odin', { 'has-error': error })}
          appendTo={appendTo}
          locale={locale}
          value={valueState}
          onChange={handleChange}
          disabled={readOnly}
          icon={icon}
          showIcon={showIcon}
          iconPos={iconPosiiton}
          showTime={showTime}
          hourFormat={hourFormat}
          timeOnly={timeOnly}
          readOnlyInput={readOnlyInput}
          showButtonBar={showButtonBar}
          selectionMode={selectionMode}
          headerTemplate={headerTemplate}
          prevIcon={prevIcon}
          nextIcon={nextIcon}
          incrementIcon={incrementIcon}
          decrementIcon={decrementIcon}
          data-testid={`calendar-component-${label}`}
          /* use year navigator as a temporary workaround until https://github.com/primefaces/primereact/issues/8258 is resolved */
          yearNavigator
          placeholder={placeholder}
        />
      )}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
}

export default OdinCalendar;
